import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';

import PropTypes from 'prop-types';
import {
  Collapse,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  IconButton,
  ListItemButton,
  Box,
  Stack,
  Typography,
  useTheme
} from '@mui/material';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import {
  reducerUpdateObjectListByObjectType,
  reducerUpdateSelectedObjectType
} from 'src/features/objectManagement';
import { reducerUpdateSelectedCampaign } from 'src/features/sosmedCampaign';
import { ICampaign } from 'src/models/campaign';
import { IGroupObject } from 'src/models/objectManagement';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import { reducerUpdateNextTourSteps } from 'src/features/productTour';
import {
  CUSTOM_DASHBOARD,
  CUSTOMIZATION,
  OFFLINE_MEDIA_STATISTIC,
  ONLINE_MEDIA_STATISTIC,
  OVERVIEW,
  SETTINGS,
  SOCIAL_MEDIA,
  SOCIAL_MEDIA_COMPARISON,
  SOCIAL_MEDIA_OVERVIEW
} from 'src/route';
import { useScrolls } from '../ScrollContext';
import { projectIds } from 'src/constants/projectIds';

interface SidebarMenuItemProps {
  children?: ReactNode;
  link?: string;
  icon?: any;
  badge?: string;
  open?: boolean;
  active?: boolean;
  name: string;
}

const Dot = styled(Box)(
  ({ theme }) => `
    height: 8px;
    width: 8px;
    background-color: ${theme.sidebar.menuItemColorActive};
    border-radius: 50%;
    display: inline-block;
    margin-right: 4px;
    transform: translateY(-2px);
  `
);

const MainMenu = styled(Typography)(
  ({ theme }) => `
    font-family: Red Hat Display;
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 130%;
    letter-spacing: -0.02em;
    color: #F5F5F5;
  `
);

const ListItemCustom = styled(ListItem)(
  ({ theme }) => `
    cursor: pointer;
    .MuiListItemButton-root {
      padding-top: 0px;
      padding-bottom: 0px;
      -webkit-transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      padding-left: 0px;
      padding-right: 0px;
      &:hover {
        background-color: unset;
      }
    }
      .MuiListItemIcon-root {
        font-family: Red Hat Display;
        font-style: normal;
        font-weight: bold;
        font-size: 16px;
        line-height: 130%;
        letter-spacing: -0.02em;
        color: #F5F5F5;
        min-width: ${theme.spacing(4)};
      }
    }
`
);

// .MuiTypography-root {
//   font-family: Red Hat Display;
//   font-style: normal;
//   font-weight: bold;
//   font-size: 16px;
//   line-height: 130%;
//   letter-spacing: -0.02em;
//   color: #F5F5F5;
// }
// .MuiListItemSecondaryAction-root {
//   font-family: Red Hat Display;
//   font-style: normal;
//   font-weight: bold;
//   font-size: 16px;
//   line-height: 130%;
//   letter-spacing: -0.02em;
//   .MuiButtonBase-root {
//     color: #F5F5F5;
//   }

const CollapseCustom = styled(Collapse)(
  ({ theme }) => `
    .MuiCollapse-wrapper {
      .MuiCollapse-wrapperInner {
        .MuiList-root {
          padding: 0px;
          .MuiListItem-root {
            padding-top: ${theme.spacing(0.6)};
          }
          .MuiListItemButton-root {
            .MuiListItemText-root {
              padding-bottom: ${theme.spacing(1.2)};
              border-bottom: 1px solid ${theme.sidebar.dividerBg};
            }
          }
        }
      }
    }
  `
);

const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
  children,
  link,
  icon: Icon,
  badge,
  open: openParent,
  active,
  name,
  ...rest
}) => {
  const [menuToggle, setMenuToggle] = useState<boolean>(openParent);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const projectId = useAppSelector((state) => state.users.project.id);
  const timezone = useAppSelector((state) => state.users.project.timezone);
  const customerType = useAppSelector(
    (state) => state.users.project.customerType
  );
  const {
    objectList,
    groupSelectedById,
    selectedGroups,
    ecommerceObjectList,
    selectedEcommerceGroups
  } = useAppSelector((state) => state.objectManagement);
  const {
    isOnTour,
    isTourButtonOpen,
    currentTour: { stepNumber, menu, submenu }
  } = useAppSelector((state) => state.storeProductTour);

  const menuName = timezone === 7 ? 'Dashboard' : 'Instant Insight';
  const isCustomDashboard = link === CUSTOM_DASHBOARD;

  // Helper functions for group and object ID extraction
  const getActiveGroups = (groups: IGroupObject[]): IGroupObject[] =>
    groupSelectedById === '_default' ? [] : groups;

  const getObjectIdsFromGroups = (groups: IGroupObject[]): string[] => {
    if (!groups.length) return [];
    return groups
      .map((group) => group.objectList.map((object) => object.id))
      .flat();
  };

  // To make bigger in main overview
  const isMainOverview = useMemo(() => {
    return name === 'Main Overview' && link === '/overview';
  }, [name, link]);

  const activeGroupObjects: IGroupObject[] = useMemo(
    () => getActiveGroups(selectedGroups),
    [groupSelectedById, selectedGroups]
  );

  const activeGroupObjectIdList: string[] = useMemo(
    () => getObjectIdsFromGroups(activeGroupObjects),
    [activeGroupObjects]
  );

  const activeEcommerceGroupObjects: IGroupObject[] = useMemo(() => {
    if (selectedEcommerceGroups.length) return selectedEcommerceGroups;
    else return [];
  }, [groupSelectedById, selectedEcommerceGroups]);

  const activeEcommerceGroupObjectIdList: string[] = useMemo(
    () => getObjectIdsFromGroups(activeEcommerceGroupObjects),
    [activeEcommerceGroupObjects]
  );

  const toggleMenu = (): void => {
    setMenuToggle((Open) => !Open);
  };

  const isCurrentlyOnTourSettings =
    name === 'Settings' && stepNumber === 2 && isOnTour && menu === 'settings';
  const isCurrentlyOnTourSocialMediaComparison =
    link === SOCIAL_MEDIA_COMPARISON &&
    stepNumber === 2 &&
    isOnTour &&
    menu === 'social-media' &&
    submenu === 'comparison';
  const isCurrentlyOnTourCustomDashboard =
    link === CUSTOM_DASHBOARD &&
    stepNumber === 2 &&
    isOnTour &&
    menu === 'customization';
  const isCurrentlyOnTourOverview =
    link === OVERVIEW && stepNumber === 2 && isOnTour && menu === 'overview';
  const isCurrentlyOnTourSocialMediaOverview =
    link === SOCIAL_MEDIA_OVERVIEW &&
    stepNumber === 2 &&
    isOnTour &&
    menu === 'social-media' &&
    submenu === 'overview';

  const isTourMode =
    isCurrentlyOnTourSettings ||
    isCurrentlyOnTourSocialMediaComparison ||
    isCurrentlyOnTourCustomDashboard ||
    isCurrentlyOnTourOverview ||
    isCurrentlyOnTourSocialMediaOverview;

  const refs = useScrolls();

  const menuRef = () => {
    if (link === SETTINGS) return refs?.sidebar?.settings;
    if (link === SOCIAL_MEDIA) return refs?.sidebar?.['social-media'];
    if (link === CUSTOMIZATION) return refs?.sidebar?.customization;
    if (link === OVERVIEW) return refs?.sidebar?.overview;
    return null;
  };

  // reset active object id when page changes
  const onClickMenuChild = (menuName: string, link: string) => {
    const pathTitle = link.split('/')[1];
    const isGoogleReview = pathTitle === 'google_review';
    const isStreamPanelSocmed = pathTitle === 'stream_panel';
    const isEcommerce = link.includes('ecommerce');

    //Product Tour
    if (isTourMode) {
      // proceed to next step in product tour
      dispatch(reducerUpdateNextTourSteps());
    }

    if (menuName === 'Dashboard') return;
    // for campaign page
    if (menuName === 'Campaign') {
      const allCampaign: ICampaign = {
        id: '_default',
        campaignName: 'All Hashtag',
        campaignObjectDetails: [],
        campaignProgress: 'ongoing',
        createdAt: new Date(),
        dateEnd: new Date(),
        dateStart: new Date(),
        projectId: projectId
      };
      dispatch(reducerUpdateSelectedCampaign(allCampaign));
      const objectListHashtag = objectList.data.filter(
        (object) => object.streamType === 'keyword' && object.isHashtag
      );
      const objectIdListHashtag = objectListHashtag.map((object) => object.id);
      dispatch(reducerUpdateObjectListByObjectType(objectListHashtag));
      dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectIdListHashtag));
      return;
    }
    // for google review page
    if (isGoogleReview) {
      const objectListGoogleReview = objectList.data.filter(
        (object) => object.socialMedia === 'googleReview'
      );
      const objectIdListGoogleReview = objectListGoogleReview.map(
        (object) => object.id
      );
      dispatch(reducerUpdateObjectListByObjectType(objectListGoogleReview));
      dispatch(
        reducerUpdateAnalyticArgumentObjectIdList(objectIdListGoogleReview)
      );
      return;
    }

    if (isStreamPanelSocmed) {
      const allObjectIds = objectList.data.map((object) => object.id);
      dispatch(
        reducerUpdateObjectListByObjectType(
          !activeGroupObjects.length
            ? objectList.data
            : selectedGroups.map((group) => group.objectList).flat()
        )
      );
      dispatch(
        reducerUpdateAnalyticArgumentObjectIdList(
          activeGroupObjectIdList ?? allObjectIds
        )
      );
      return;
    }

    if (isEcommerce) {
      const allEcommerceObjectIdList = ecommerceObjectList?.map(
        (object) => object.id
      );
      dispatch(
        reducerUpdateObjectListByObjectType(
          !activeEcommerceGroupObjects.length
            ? ecommerceObjectList
            : selectedEcommerceGroups.map((group) => group.objectList).flat()
        )
      );
      dispatch(
        reducerUpdateAnalyticArgumentObjectIdList(
          activeEcommerceGroupObjectIdList.length > 0
            ? activeEcommerceGroupObjectIdList
            : allEcommerceObjectIdList
        )
      );
      return;
    }

    // for other pages
    const objectListWithoutGoogleReview = objectList.data.filter(
      (object) => object.socialMedia !== 'googleReview'
    );
    const objectIdListWithoutGoogleReview = objectListWithoutGoogleReview.map(
      (object) => object.id
    );
    dispatch(reducerUpdateSelectedObjectType('all'));
    dispatch(
      reducerUpdateObjectListByObjectType(
        !activeGroupObjects.length
          ? objectListWithoutGoogleReview
          : selectedGroups.map((group) => group.objectList).flat()
      )
    );
    dispatch(
      reducerUpdateAnalyticArgumentObjectIdList(
        activeGroupObjectIdList ?? objectIdListWithoutGoogleReview
      )
    );
  };

  useEffect(() => {
    if (isTourButtonOpen) {
      setMenuToggle(true);
    }
  }, [isTourButtonOpen]);

  if (
    projectId === projectIds.kpk &&
    (link === ONLINE_MEDIA_STATISTIC || link === OFFLINE_MEDIA_STATISTIC)
  )
    return <></>;

  if (name === 'AI Assistant' && customerType === 'b2c') return null;

  if (children) {
    return (
      <>
        <ListItemCustom
          key={name}
          {...rest}
          onClick={toggleMenu}
          secondaryAction={
            <IconButton edge="end" aria-label="delete" sx={{ padding: 0 }}>
              {menuToggle ? (
                <ArrowDropUpIcon sx={{ color: '#FBFBFB' }} />
              ) : (
                <ArrowDropDownIcon sx={{ color: '#FBFBFB' }} />
              )}
            </IconButton>
          }
          ref={menuRef()}
        >
          <ListItemIcon>{Icon && <Icon />}</ListItemIcon>
          <ListItemText
            primary={<MainMenu>{isCustomDashboard ? menuName : name}</MainMenu>}
          />
        </ListItemCustom>
        <CollapseCustom in={menuToggle}>{children}</CollapseCustom>
      </>
    );
  }

  return (
    <ListItemCustom key={name} {...rest} ref={menuRef()}>
      <ProductTourTooltip placement="left-end" open={isTourMode}>
        <ListItemButton
          onClick={() => onClickMenuChild(name, link)}
          component={RouterLink}
          to={link}
          activeClassName="Mui-active"
          sx={
            isTourMode
              ? {
                  position: 'relative',
                  zIndex: 1000,
                  border: 1,
                  borderColor: 'white'
                }
              : {}
          }
        >
          <ListItemIcon>{Icon && <Icon />}</ListItemIcon>
          <ListItemText
            primary={
              active ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Dot />
                  {isMainOverview ? (
                    <MainMenu sx={{ color: theme.sidebar.menuItemColorActive }}>
                      Overview
                    </MainMenu>
                  ) : (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.sidebar.menuItemColorActive,
                        fontWeight: 600
                      }}
                    >
                      {isCustomDashboard ? menuName : name}
                    </Typography>
                  )}
                </Stack>
              ) : isMainOverview ? (
                <MainMenu>Overview</MainMenu>
              ) : (
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#FBFBFB',
                    fontWeight: 600
                  }}
                >
                  {isCustomDashboard ? menuName : name}
                </Typography>
              )
            }
            primaryTypographyProps={
              active ? { style: { color: '#009FE3' } } : {}
            }
          />
        </ListItemButton>
      </ProductTourTooltip>
    </ListItemCustom>
  );
};

SidebarMenuItem.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
  link: PropTypes.string,
  icon: PropTypes.elementType,
  badge: PropTypes.string,
  open: PropTypes.bool,
  name: PropTypes.string.isRequired
};

SidebarMenuItem.defaultProps = {
  open: false,
  active: false
};

export default SidebarMenuItem;
